import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  overflow: hidden;
`;

const ModalContent = styled(motion.div)`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.3);
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.2rem;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #777;
  padding: 5px;
  line-height: 1;
  &:hover {
    color: #333;
  }
`;

const FilePreviewArea = styled.div`
  flex-grow: 1;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f0f0;
  position: relative;
  min-height: 300px;
`;

const StyledImage = styled.img<{ rotation?: number; scale?: number; panX?: number; panY?: number; isPanning?: boolean }>`
  max-width: 100%;
  max-height: calc(90vh - 200px);
  object-fit: contain;
  transform: ${props => `rotate(${props.rotation || 0}deg) scale(${props.scale || 1}) translate(${props.panX || 0}px, ${props.panY || 0}px)`};
  transition: ${props => props.isPanning ? 'none' : 'transform 0.3s ease'};
  cursor: grab;
  user-select: none;
  &:active {
    cursor: grabbing;
  }
`;

const CropCanvas = styled.canvas`
  max-width: 100%;
  max-height: calc(90vh - 200px);
  cursor: crosshair;
`;

const ImageToolbar = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 10px;
  background: #f5f5f5;
  border-top: 1px solid #e0e0e0;
  flex-wrap: wrap;
`;

const ToolButton = styled.button<{ active?: boolean; variant?: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 1px solid ${props => props.active ? '#4A6CF7' : '#ddd'};
  border-radius: 6px;
  background: ${props => props.active ? '#4A6CF7' : props.variant === 'save' ? '#22c55e' : 'white'};
  cursor: pointer;
  font-size: 0.85rem;
  color: ${props => props.active || props.variant === 'save' ? 'white' : '#333'};
  transition: all 0.2s;
  &:hover {
    background: ${props => props.active ? '#3b5de7' : props.variant === 'save' ? '#16a34a' : '#e8e8e8'};
    border-color: ${props => props.active ? '#3b5de7' : '#ccc'};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ZoomDisplay = styled.span`
  padding: 6px 12px;
  background: #e0e0e0;
  border-radius: 6px;
  font-size: 0.85rem;
  color: #333;
  min-width: 60px;
  text-align: center;
`;

const StyledPdfIframe = styled.iframe`
  width: 100%;
  height: calc(90vh - 120px);
  border: none;
`;

const ErrorMessage = styled.p`
  color: red;
  text-align: center;
`;

const spinKeyframes = `
  @keyframes modalSpinner {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  @keyframes modalPulse {
    0%, 100% { opacity: 0.7; }
    50% { opacity: 1; }
  }
  @keyframes modalCheckmark {
    0% { transform: scale(0) rotate(-45deg); opacity: 0; }
    50% { transform: scale(1.2) rotate(-45deg); opacity: 1; }
    100% { transform: scale(1) rotate(-45deg); opacity: 1; }
  }
`;

const SavingOverlay = styled.div<{ $success?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.$success ? 'rgba(39, 174, 96, 0.9)' : 'rgba(255,255,255,0.92)'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  font-size: 1.1rem;
  color: ${props => props.$success ? '#fff' : '#333'};
  font-weight: 500;
  z-index: 10;
  backdrop-filter: blur(2px);
  transition: background 0.3s ease;

  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #e0e0e0;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    animation: modalSpinner 0.8s linear infinite;
  }

  .checkmark {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    &::after {
      content: '';
      display: block;
      width: 16px;
      height: 28px;
      border: solid #27ae60;
      border-width: 0 4px 4px 0;
      animation: modalCheckmark 0.4s ease forwards;
    }
  }

  .saving-text {
    animation: modalPulse 1.2s ease-in-out infinite;
  }
`;

interface FilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileUrl: string | null;
  fileName: string | null;
  fileType?: string | null;
  applicationId?: string;
  fieldName?: string;
  onSaveSuccess?: (newFilePath: string) => void;
  // Optional: when provided, modal works in generic image-editing mode
  // and delegates saving to this callback instead of calling the
  // /api/applications/:applicationId/upload-file/:fieldName endpoint.
  onSaveEditedImage?: (dataUrl: string) => Promise<void>;
  // Optional: set to "/api/admin" for admin routes (default: "/api")
  apiPrefix?: string;
}

interface HistoryState {
  imageData: string;
  rotation: number;
}

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  isOpen,
  onClose,
  fileUrl,
  fileName,
  fileType,
  applicationId,
  fieldName,
  onSaveSuccess,
  onSaveEditedImage,
  apiPrefix = '/api',
}) => {
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);
  const [isCropMode, setIsCropMode] = useState(false);
  const [cropStart, setCropStart] = useState<{ x: number; y: number } | null>(null);
  const [cropEnd, setCropEnd] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [currentImageData, setCurrentImageData] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryState[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Pan/drag state for zoomed images
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<{ x: number; y: number } | null>(null);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const previewAreaRef = useRef<HTMLDivElement>(null);

  const canEdit = !!onSaveEditedImage || (!!applicationId && !!fieldName && !!onSaveSuccess);

  // Load image when modal opens
  useEffect(() => {
    if (isOpen && fileUrl && fileType?.startsWith('image/')) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        imageRef.current = img;
        setCurrentImageData(fileUrl);
        setHistory([{ imageData: fileUrl, rotation: 0 }]);
        setHistoryIndex(0);
        setHasChanges(false);
      };
      img.src = fileUrl;
    }
  }, [isOpen, fileUrl, fileType]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setRotation(0);
      setScale(1);
      setIsCropMode(false);
      setCropStart(null);
      setCropEnd(null);
      setCurrentImageData(null);
      setHistory([]);
      setHistoryIndex(-1);
      setHasChanges(false);
      setIsSaving(false);
      setSaveSuccess(false);
      setPanX(0);
      setPanY(0);
      setIsPanning(false);
      setPanStart(null);
    }
  }, [isOpen]);

  // Inject keyframes CSS for spinner animation
  useEffect(() => {
    const styleId = 'file-preview-modal-keyframes';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = spinKeyframes;
      document.head.appendChild(style);
    }
  }, []);
  
  // Pan handlers — work at any zoom level for dragging the image around
  const handleImageMouseDown = useCallback((e: React.MouseEvent) => {
    if (!isCropMode) {
      e.preventDefault();
      setIsPanning(true);
      setPanStart({ x: e.clientX - panX, y: e.clientY - panY });
    }
  }, [isCropMode, panX, panY]);

  const handleImageMouseMove = useCallback((e: React.MouseEvent) => {
    if (isPanning && panStart) {
      e.preventDefault();
      setPanX(e.clientX - panStart.x);
      setPanY(e.clientY - panStart.y);
    }
  }, [isPanning, panStart]);
  
  const handleImageMouseUp = useCallback(() => {
    setIsPanning(false);
    setPanStart(null);
  }, []);

  // Scroll-to-zoom: mouse wheel zooms in/out
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? -0.15 : 0.15;
    setScale(prev => {
      const next = Math.round((prev + delta) * 100) / 100;
      return Math.max(0.25, Math.min(5, next));
    });
  }, []);

  // Attach wheel listener to the preview area (passive: false to allow preventDefault)
  useEffect(() => {
    const el = previewAreaRef.current;
    if (!el || !isOpen) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [isOpen, handleWheel]);

  // Draw canvas when in crop mode
  useEffect(() => {
    if (isCropMode && canvasRef.current && currentImageData) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        // Calculate display size
        const maxWidth = Math.min(700, window.innerWidth * 0.8);
        const maxHeight = window.innerHeight * 0.5;
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        // Draw crop overlay
        if (cropStart && cropEnd) {
          const x = Math.min(cropStart.x, cropEnd.x);
          const y = Math.min(cropStart.y, cropEnd.y);
          const w = Math.abs(cropEnd.x - cropStart.x);
          const h = Math.abs(cropEnd.y - cropStart.y);

          // Darken outside crop area
          ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
          ctx.fillRect(0, 0, canvas.width, y);
          ctx.fillRect(0, y + h, canvas.width, canvas.height - y - h);
          ctx.fillRect(0, y, x, h);
          ctx.fillRect(x + w, y, canvas.width - x - w, h);

          // Draw crop border
          ctx.strokeStyle = '#4A6CF7';
          ctx.lineWidth = 2;
          ctx.setLineDash([5, 5]);
          ctx.strokeRect(x, y, w, h);
        }
      };
      img.src = currentImageData;
    }
  }, [isCropMode, currentImageData, cropStart, cropEnd]);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isCropMode) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setCropStart({ x, y });
    setCropEnd({ x, y });
    setIsDragging(true);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging || !isCropMode) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, canvas.width));
    const y = Math.max(0, Math.min(e.clientY - rect.top, canvas.height));
    setCropEnd({ x, y });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const applyCrop = useCallback(() => {
    if (!cropStart || !cropEnd || !currentImageData) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const scaleX = img.width / canvas.width;
      const scaleY = img.height / canvas.height;

      const x = Math.min(cropStart.x, cropEnd.x) * scaleX;
      const y = Math.min(cropStart.y, cropEnd.y) * scaleY;
      const w = Math.abs(cropEnd.x - cropStart.x) * scaleX;
      const h = Math.abs(cropEnd.y - cropStart.y) * scaleY;

      if (w < 10 || h < 10) return;

      const cropCanvas = document.createElement('canvas');
      cropCanvas.width = w;
      cropCanvas.height = h;
      const cropCtx = cropCanvas.getContext('2d');
      if (!cropCtx) return;

      cropCtx.drawImage(img, x, y, w, h, 0, 0, w, h);
      const newImageData = cropCanvas.toDataURL('image/jpeg', 0.9);

      setCurrentImageData(newImageData);
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push({ imageData: newImageData, rotation: 0 });
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
      setHasChanges(true);
      setCropStart(null);
      setCropEnd(null);
      setIsCropMode(false);
    };
    img.src = currentImageData;
  }, [cropStart, cropEnd, currentImageData, history, historyIndex]);

  const applyRotation = useCallback((degrees: number) => {
    if (!currentImageData) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const isVertical = Math.abs(degrees) % 180 !== 0;
      canvas.width = isVertical ? img.height : img.width;
      canvas.height = isVertical ? img.width : img.height;

      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((degrees * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      const newImageData = canvas.toDataURL('image/jpeg', 0.9);
      setCurrentImageData(newImageData);
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push({ imageData: newImageData, rotation: 0 });
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
      setHasChanges(true);
    };
    img.src = currentImageData;
  }, [currentImageData, history, historyIndex]);

  const handleUndo = () => {
    if (historyIndex > 0) {
      const prevState = history[historyIndex - 1];
      setCurrentImageData(prevState.imageData);
      setRotation(prevState.rotation);
      setHistoryIndex(historyIndex - 1);
      setHasChanges(historyIndex - 1 > 0);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const nextState = history[historyIndex + 1];
      setCurrentImageData(nextState.imageData);
      setRotation(nextState.rotation);
      setHistoryIndex(historyIndex + 1);
      setHasChanges(true);
    }
  };

  // Helper: show success overlay briefly then auto-close
  const showSuccessAndClose = useCallback(() => {
    setIsSaving(false);
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      onClose();
    }, 1000); // show success checkmark for 1 second
  }, [onClose]);

  const handleSave = async () => {
    if (!currentImageData) return;

    setIsSaving(true);
    setSaveSuccess(false);
    try {
      // Generic image-editing mode: delegate saving to caller
      if (onSaveEditedImage) {
        await onSaveEditedImage(currentImageData);
        setHasChanges(false);
        showSuccessAndClose();
        return;
      }

      // Default document mode: upload edited image to application endpoint
      if (!applicationId || !fieldName || !onSaveSuccess) return;

      // Convert base64 to blob
      const response = await fetch(currentImageData);
      const blob = await response.blob();

      // Create form data - ensure filename always has a valid image extension
      const formData = new FormData();
      // Derive extension from the blob MIME type (most reliable after canvas export)
      const mimeToExt: Record<string, string> = {
        'image/jpeg': 'jpg', 'image/png': 'png', 'image/gif': 'gif',
        'image/bmp': 'bmp', 'image/webp': 'webp',
      };
      const blobExt = mimeToExt[blob.type] || 'jpg';
      // Check if fileName already has a valid image extension
      const hasValidExt = fileName ? /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileName) : false;
      const safeFileName = hasValidExt ? fileName! : `${(fileName || 'edited').replace(/[^a-zA-Z0-9_\-#\s]/g, '')}.${blobExt}`;
      formData.append('formFile', blob, safeFileName);

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const token = sessionStorage.getItem('userToken');

      // Construct the base URL, removing trailing /api if present to avoid duplication
      let baseUrl = apiUrl;
      if (baseUrl.endsWith('/api')) {
        baseUrl = baseUrl.slice(0, -4);
      }

      // Use apiPrefix to support both user (/api) and admin (/api/admin) routes
      const uploadResponse = await fetch(`${baseUrl}${apiPrefix}/applications/${applicationId}/upload-file/${fieldName}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json().catch(() => ({}));
        throw new Error(errorData.message || 'Upload failed');
      }

      const result = await uploadResponse.json();
      onSaveSuccess(result.filePath);
      setHasChanges(false);
      showSuccessAndClose();
    } catch (error) {
      console.error('Failed to save image:', error);
      alert('Failed to save image. Please try again.');
      setIsSaving(false);
    }
  };

  if (!isOpen || !fileUrl) return null;

  const isImage = fileType?.startsWith('image/');
  const isPdf = fileType === 'application/pdf';

  const handleRotateLeft = () => applyRotation(-90);
  const handleRotateRight = () => applyRotation(90);
  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.25));
  const handleReset = () => { setScale(1); setPanX(0); setPanY(0); };

  // Use Portal to render modal at document body level for proper viewport centering
  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContent
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -50, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>{fileName || 'File Preview'}</ModalTitle>
              <CloseButton onClick={onClose}>&times;</CloseButton>
            </ModalHeader>
            <FilePreviewArea ref={previewAreaRef}>
              {(isSaving || saveSuccess) && (
                <SavingOverlay $success={saveSuccess}>
                  {saveSuccess ? (
                    <>
                      <div className="checkmark" />
                      <span>Saved successfully!</span>
                    </>
                  ) : (
                    <>
                      <div className="spinner" />
                      <span className="saving-text">Please wait... saving data</span>
                    </>
                  )}
                </SavingOverlay>
              )}
              {isImage && !isCropMode && (
                <StyledImage 
                  src={currentImageData || fileUrl} 
                  alt={fileName || 'Preview'} 
                  rotation={rotation} 
                  scale={scale}
                  panX={panX}
                  panY={panY}
                  isPanning={isPanning}
                  onMouseDown={handleImageMouseDown}
                  onMouseMove={handleImageMouseMove}
                  onMouseUp={handleImageMouseUp}
                  onMouseLeave={handleImageMouseUp}
                  draggable={false}
                />
              )}
              {isImage && isCropMode && (
                <CropCanvas
                  ref={canvasRef}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                />
              )}
              {isPdf && (
                <div style={{ width: '100%', height: 'calc(90vh - 120px)', display: 'flex', flexDirection: 'column' }}>
                  <StyledPdfIframe 
                    src={`${fileUrl}#toolbar=1&navpanes=0`} 
                    title="PDF Preview"
                  />
                  <div style={{ padding: '12px', textAlign: 'center', borderTop: '1px solid #eee' }}>
                    <a 
                      href={fileUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        background: '#1a5276', 
                        color: 'white', 
                        padding: '10px 20px', 
                        borderRadius: '6px', 
                        textDecoration: 'none',
                        display: 'inline-block',
                        fontSize: '0.9em'
                      }}
                    >
                      📄 Open PDF in New Tab
                    </a>
                  </div>
                </div>
              )}
              {!isImage && !isPdf && (
                <ErrorMessage>
                  Cannot preview this file type. Supported types: Images, PDF.
                </ErrorMessage>
              )}
            </FilePreviewArea>
            {isImage && (
              <ImageToolbar>
                {canEdit && (
                  <>
                    <ToolButton onClick={handleUndo} disabled={historyIndex <= 0} title="Undo">
                      ↶ Undo
                    </ToolButton>
                    <ToolButton onClick={handleRedo} disabled={historyIndex >= history.length - 1} title="Redo">
                      ↷ Redo
                    </ToolButton>
                    <ToolButton 
                      onClick={() => { setIsCropMode(!isCropMode); setCropStart(null); setCropEnd(null); }} 
                      active={isCropMode}
                      title="Crop"
                    >
                      ✂ Crop
                    </ToolButton>
                    {isCropMode && cropStart && cropEnd && (
                      <ToolButton onClick={applyCrop} variant="save" title="Apply Crop">
                        ✓ Apply
                      </ToolButton>
                    )}
                  </>
                )}
                {!isCropMode && (
                  <>
                    <ToolButton onClick={handleRotateLeft} title="Rotate Left">
                      ↺ Rotate Left
                    </ToolButton>
                    <ToolButton onClick={handleRotateRight} title="Rotate Right">
                      ↻ Rotate Right
                    </ToolButton>
                    <ToolButton onClick={handleZoomOut} title="Zoom Out" disabled={scale <= 0.25}>
                      − Zoom Out
                    </ToolButton>
                    <ZoomDisplay>{Math.round(scale * 100)}%</ZoomDisplay>
                    <ToolButton onClick={handleZoomIn} title="Zoom In" disabled={scale >= 3}>
                      + Zoom In
                    </ToolButton>
                    <ToolButton onClick={handleReset} title="Reset Zoom">
                      ⟳ Reset
                    </ToolButton>
                  </>
                )}
                {canEdit && hasChanges && !isCropMode && (
                  <ToolButton onClick={handleSave} variant="save" disabled={isSaving} title="Save Changes">
                    💾 Save
                  </ToolButton>
                )}
              </ImageToolbar>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default FilePreviewModal;
